import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';

const Inventory = ({ apiBase, user }) => {
  const { tenant } = useTenant();

  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');
  const [filters, setFilters] = useState({
    building: '',
    wing: '',
    tower_type: '',
    floor: '',
    status: '',
    bhk_type: '',
    search: ''
  });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [selectedTower, setSelectedTower] = useState('all'); // 'all' or specific tower
  const [editingUnit, setEditingUnit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('edit'); // edit, block, book, cancel, remarks, links

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchInventory();
    fetchStats();
  }, [selectedProject, filters]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${apiBase}/projects`);
      setProjects(res.data);
      if (res.data.length > 0 && !selectedProject) {
        // Auto-select first project
        setSelectedProject(res.data[0].id.toString());
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      alert('Error loading projects: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchInventory = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { ...filters };
      if (selectedProject) {
        params.project_id = selectedProject;
      }
      const res = await axios.get(`${apiBase}/inventory`, { params });
      setUnits(res.data || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      if (!silent) setUnits([]);
      // Don't show alert if it's just no project selected
      if (selectedProject && !silent) {
        alert('Error loading inventory: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = selectedProject ? { project_id: selectedProject } : {};
      const res = await axios.get(`${apiBase}/inventory/stats`, { params });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleAction = (unit, action) => {
    setEditingUnit(unit);
    setModalType(action);
    setShowModal(true);
  };

  const handleBlock = async (unitId, data) => {
    try {
      await axios.patch(`${apiBase}/inventory/${unitId}/block`, data);
      fetchInventory();
      fetchStats();
      setShowModal(false);
    } catch (error) {
      alert('Error blocking unit: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUnblock = async (unitId) => {
    try {
      await axios.patch(`${apiBase}/inventory/${unitId}/unblock`);
      fetchInventory();
      fetchStats();
    } catch (error) {
      alert('Error unblocking unit: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleBook = async (unitId, data) => {
    try {
      // Get unit details to calculate agreement value
      const unitResponse = await axios.get(`${apiBase}/inventory/${unitId}`);
      const unit = unitResponse.data;
      const area = unit.carpet_area || 0;
      const agreementValue = area * 25000; // PSF rate: 25000

      // Include agreement value in booking data
      const bookingData = {
        ...data,
        agreement_value: agreementValue,
        area: area
      };

      await axios.patch(`${apiBase}/inventory/${unitId}/book`, bookingData);
      fetchInventory();
      fetchStats();
      setShowModal(false);
    } catch (error) {
      alert('Error booking unit: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancel = async (unitId, data) => {
    try {
      await axios.patch(`${apiBase}/inventory/${unitId}/cancel`, data);
      fetchInventory();
      fetchStats();
      setShowModal(false);
    } catch (error) {
      alert('Error cancelling unit: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdate = async (unitId, data) => {
    try {
      await axios.put(`${apiBase}/inventory/${unitId}`, data);
      fetchInventory();
      fetchStats();
      setShowModal(false);
    } catch (error) {
      alert('Error updating unit: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateRemarks = async (unitId, remarks) => {
    try {
      await axios.patch(`${apiBase}/inventory/${unitId}/remarks`, { remarks });
      fetchInventory();
      setShowModal(false);
    } catch (error) {
      alert('Error updating remarks: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateHyperlinks = async (unitId, data) => {
    try {
      await axios.patch(`${apiBase}/inventory/${unitId}/hyperlinks`, data);
      fetchInventory();
      setShowModal(false);
    } catch (error) {
      alert('Error updating hyperlinks: ' + (error.response?.data?.error || error.message));
    }
  };

  const initializeInventory = async () => {
    if (!selectedProject) {
      alert('Please select a project first');
      return;
    }
    if (!window.confirm('This will create 4 wings x 35 floors x 6 flats = 840 units. Continue?')) {
      return;
    }
    try {
      await axios.post(`${apiBase}/inventory/bulk-create`, {
        project_id: selectedProject,
        wings: ['A', 'B', 'C', 'D'],
        floors_per_wing: 35,
        flats_per_floor: 6,
        bhk_distribution: { '1BHK': 3, '2BHK': 3 }
      });
      alert('Inventory initialized successfully!');
      fetchInventory();
      fetchStats();
    } catch (error) {
      alert('Error initializing inventory: ' + (error.response?.data?.error || error.message));
    }
  };

  // Unit status colour code (2026-09-09). One vocabulary for the grid, the
  // badges, the filter and the summary tiles. 'blocked' is the legacy spelling
  // of 'hold' and is kept so the existing block/unblock endpoints keep working.
  const STATUS_CODE = {
    available: { label: 'Available', colour: '#10b981' },
    hold: { label: 'Hold', colour: '#f59e0b' },
    blocked: { label: 'Hold', colour: '#f59e0b' },
    payment_pending: { label: 'Payment pending', colour: '#8b5cf6' },
    booked: { label: 'Booked', colour: '#2563eb' },
    sold: { label: 'Sold', colour: '#0f766e' },
    cancelled: { label: 'Cancelled', colour: '#ef4444' },
    refuge: { label: 'Not for sale', colour: '#6b7280' }
  };

  const getStatusColor = (status) => (STATUS_CODE[status] || {}).colour || '#6b7280';
  const getStatusLabel = (status) => (STATUS_CODE[status] || {}).label || (status || 'unknown');

  const getStatusBadge = (status) => {
    return (
      <span className="badge" style={{ backgroundColor: getStatusColor(status) + '20', color: getStatusColor(status) }}>
        {getStatusLabel(status).toUpperCase()}
      </span>
    );
  };

  // Get unique values for filters
  const buildings = [...new Set(units.map(u => u.building || u.wing).filter(Boolean))].sort();
  const wings = [...new Set(units.map(u => u.wing).filter(Boolean))].sort();
  const towerTypes = [...new Set(units.map(u => u.tower_type).filter(Boolean))].sort();
  const floors = [...new Set(units.map(u => u.floor))].sort((a, b) => a - b);
  const statuses = ['available', 'hold', 'payment_pending', 'booked', 'sold', 'cancelled', 'refuge'];
  const bhkTypes = [...new Set(units.map(u => u.bhk_type).filter(Boolean))].sort();

  // Apply all filters to units
  let filteredUnits = units;

  // Apply tower filter
  if (selectedTower !== 'all') {
    const [building, tower] = selectedTower.split('-');
    filteredUnits = filteredUnits.filter(u =>
      (u.building === building || u.wing === building) &&
      (tower === 'DEFAULT' || u.tower_type === tower)
    );
  }

  // Apply other filters
  if (filters.building) {
    filteredUnits = filteredUnits.filter(u => (u.building || u.wing) === filters.building);
  }
  if (filters.wing) {
    filteredUnits = filteredUnits.filter(u => u.wing === filters.wing);
  }
  if (filters.tower_type) {
    filteredUnits = filteredUnits.filter(u => u.tower_type === filters.tower_type);
  }
  if (filters.floor) {
    filteredUnits = filteredUnits.filter(u => String(u.floor) === String(filters.floor));
  }
  if (filters.status) {
    filteredUnits = filteredUnits.filter(u => u.status === filters.status);
  }
  if (filters.bhk_type) {
    filteredUnits = filteredUnits.filter(u => u.bhk_type === filters.bhk_type);
  }
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filteredUnits = filteredUnits.filter(u =>
      u.unit_number?.toLowerCase().includes(searchLower) ||
      u.flat_number?.toLowerCase().includes(searchLower)
    );
  }

  // Group units by building/wing and tower for grid view
  const groupedUnits = {};
  filteredUnits.forEach(unit => {
    const building = unit.building || unit.wing || 'Unknown';
    const tower = unit.tower_type || 'DEFAULT';
    const key = `${building}-${tower}`;
    if (!groupedUnits[key]) {
      groupedUnits[key] = {
        building,
        tower,
        units: []
      };
    }
    groupedUnits[key].units.push(unit);
  });

  // Get all unique building-tower combinations
  const buildingTowerCombos = Object.keys(groupedUnits).sort();

  const selectedProjectName = projects.find(p => p.id.toString() === selectedProject)?.name || '';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1>Inventory Management</h1>
          {selectedProjectName && (
            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '16px', fontWeight: '500' }}>
              Project: <span style={{ color: '#2563eb' }}>{selectedProjectName}</span>
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="form-select"
            style={{ minWidth: '200px' }}
          >
            <option value="">All Projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id.toString()}>{p.name}</option>
            ))}
          </select>
          {selectedProject && units.length === 0 && (
            <button onClick={initializeInventory} className="btn btn-primary">
              Initialize Inventory (4 Wings x 35 Floors)
            </button>
          )}
          {selectedProject && units.length > 0 && (
            <span style={{ fontSize: '12px', color: '#6e6e73' }}>
              {units.length} units loaded
            </span>
          )}
          {projects.length === 0 && (
            <div style={{ padding: '8px 16px', background: '#fef3c7', color: '#92400e', borderRadius: '6px', fontSize: '14px' }}>
              No projects found. Restart server to create tenant.name
            </div>
          )}
        </div>
      </div>

      {/* Status colour code */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center',
                    padding: '10px 14px', marginBottom: '16px', background: '#fff',
                    border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>Status key</span>
        {['available', 'hold', 'payment_pending', 'booked', 'sold', 'cancelled', 'refuge'].map(k => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '3px',
                           background: STATUS_CODE[k].colour, border: '1px solid rgba(0,0,0,0.1)' }} />
            {STATUS_CODE[k].label}
          </span>
        ))}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-label">Total Units</div>
            <div className="stat-value">{stats.summary?.total_units || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Available</div>
            <div className="stat-value" style={{ color: '#10b981' }}>{stats.summary?.available || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Hold</div>
            <div className="stat-value" style={{ color: '#f59e0b' }}>{stats.summary?.blocked || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Payment pending</div>
            <div className="stat-value" style={{ color: '#8b5cf6' }}>{stats.summary?.payment_pending || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Booked</div>
            <div className="stat-value" style={{ color: '#2563eb' }}>{stats.summary?.booked || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Sold</div>
            <div className="stat-value" style={{ color: '#0f766e' }}>{stats.summary?.sold || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Cancelled</div>
            <div className="stat-value" style={{ color: '#ef4444' }}>{stats.summary?.cancelled || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Not for sale</div>
            <div className="stat-value" style={{ color: '#6b7280' }}>{stats.summary?.refuge || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total Value</div>
            <div className="stat-value">₹{formatCurrency(stats.summary?.total_value || 0)}</div>
          </div>
        </div>
      )}

      {/* View Mode Toggle and Tower Quick Filters */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="card-title" style={{ marginBottom: 0 }}>View Options</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('grid')}
            >
              📊 Grid View (All Towers)
            </button>
            <button
              className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('table')}
            >
              📋 Table View
            </button>
          </div>
        </div>

        {/* Tower Quick Filters */}
        <div style={{ marginBottom: '16px' }}>
          <label className="form-label" style={{ marginBottom: '8px', display: 'block', fontWeight: '500' }}>
            Quick Filter by Tower/Building:
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className={`btn ${selectedTower === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setSelectedTower('all')}
              style={{ fontSize: '13px', padding: '6px 12px' }}
            >
              All ({units.length})
            </button>
            {buildingTowerCombos.map(combo => {
              const [building, tower] = combo.split('-');
              const count = groupedUnits[combo]?.units.length || 0;
              return (
                <button
                  key={combo}
                  className={`btn ${selectedTower === combo ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSelectedTower(combo)}
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  {building} {tower !== 'DEFAULT' ? `- ${tower}` : ''} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Detailed Filters */}
        <details style={{ marginTop: '16px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: '500', padding: '8px', background: '#f3f4f6', borderRadius: '6px' }}>
            Advanced Filters
          </summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px', padding: '16px', background: '#f9fafb', borderRadius: '6px' }}>
            <div className="form-group">
              <label className="form-label">Building</label>
              <select
                value={filters.building}
                onChange={(e) => {
                  handleFilterChange('building', e.target.value);
                  setSelectedTower('all');
                }}
                className="form-select"
              >
                <option value="">All Buildings</option>
                {buildings.map(b => (
                  <option key={b} value={b}>Building {b}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Wing</label>
              <select
                value={filters.wing}
                onChange={(e) => {
                  handleFilterChange('wing', e.target.value);
                  setSelectedTower('all');
                }}
                className="form-select"
              >
                <option value="">All Wings</option>
                {wings.map(w => (
                  <option key={w} value={w}>Wing {w}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tower Type</label>
              <select
                value={filters.tower_type}
                onChange={(e) => {
                  handleFilterChange('tower_type', e.target.value);
                  setSelectedTower('all');
                }}
                className="form-select"
              >
                <option value="">All Towers</option>
                {towerTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Floor</label>
              <select
                value={filters.floor}
                onChange={(e) => handleFilterChange('floor', e.target.value)}
                className="form-select"
              >
                <option value="">All Floors</option>
                {floors.map(f => (
                  <option key={f} value={f}>Floor {f}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="form-select"
              >
                <option value="">All Status</option>
                {statuses.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">BHK Type</label>
              <select
                value={filters.bhk_type}
                onChange={(e) => handleFilterChange('bhk_type', e.target.value)}
                className="form-select"
              >
                <option value="">All Types</option>
                {bhkTypes.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Unit/Flat number"
                className="form-input"
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setFilters({
                    building: '',
                    wing: '',
                    tower_type: '',
                    floor: '',
                    status: '',
                    bhk_type: '',
                    search: ''
                  });
                  setSelectedTower('all');
                }}
                style={{ width: '100%' }}
              >
                Clear All Filters
              </button>
            </div>
          </div>
        </details>
      </div>

      {/* Grid View - All Towers at a Glance */}
      {viewMode === 'grid' && !loading && (
        <div>
          {buildingTowerCombos.length === 0 ? (
            <div className="card">
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <p style={{ marginBottom: '16px', fontSize: '16px' }}>No units found with current filters.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setFilters({
                      building: '',
                      wing: '',
                      tower_type: '',
                      floor: '',
                      status: '',
                      bhk_type: '',
                      search: ''
                    });
                    setSelectedTower('all');
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          ) : (
            buildingTowerCombos.map(combo => {
              const group = groupedUnits[combo];
              if (!group || group.units.length === 0) return null;

              // Group by floor for better display
              const unitsByFloor = {};
              group.units.forEach(unit => {
                const floor = unit.floor || 0;
                if (!unitsByFloor[floor]) {
                  unitsByFloor[floor] = [];
                }
                unitsByFloor[floor].push(unit);
              });

              const floors = Object.keys(unitsByFloor).map(Number).sort((a, b) => b - a);

              return (
                <div key={combo} className="card" style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 className="card-title" style={{ marginBottom: 0 }}>
                      {group.building} {group.tower !== 'DEFAULT' ? `- ${group.tower}` : ''}
                      <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b', marginLeft: '8px' }}>
                        ({group.units.length} units)
                      </span>
                    </h2>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setSelectedTower(combo);
                        setViewMode('table');
                      }}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      View Details →
                    </button>
                  </div>

                  {/* Floor-wise Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                    {floors.map(floor => {
                      const floorUnits = unitsByFloor[floor].sort((a, b) => {
                        const flatA = parseInt(a.flat_number) || 0;
                        const flatB = parseInt(b.flat_number) || 0;
                        return flatA - flatB;
                      });

                      return (

                        <div key={floor} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          padding: '8px',
                          background: '#f9fafb'
                        }}>
                          <div style={{
                            fontWeight: '600',
                            fontSize: '12px',
                            color: '#374151',
                            marginBottom: '6px',
                            textAlign: 'center',
                            padding: '4px',
                            background: '#e5e7eb',
                            borderRadius: '4px'
                          }}>
                            Floor {floor}
                          </div>
                          <div className="grid-units-container" style={{ display: 'grid', gap: '8px', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))' }}>
                            {floorUnits.map(unit => {
                              const area = unit.carpet_area || 0;
                              const agreementValue = area * 25000;
                              return (
                                <div
                                  key={unit.id}
                                  onClick={() => handleAction(unit, 'edit')}
                                  style={{
                                    padding: '8px',
                                    background: getStatusColor(unit.status) === '#10b981' ? '#d1fae5' :
                                      getStatusColor(unit.status) === '#f59e0b' ? '#fef3c7' :
                                        getStatusColor(unit.status) === '#2563eb' ? '#dbeafe' :
                                          getStatusColor(unit.status) === '#ef4444' ? '#fee2e2' : '#f3f4f6',
                                    border: `2px solid ${getStatusColor(unit.status)}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    transition: 'all 0.2s',
                                    minHeight: '60px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                  }}
                                  title={`${unit.unit_number} - ${unit.bhk_type} - ${unit.status} - ₹${formatCurrency(agreementValue)}`}
                                >
                                  <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '14px' }}>
                                    {unit.flat_number || unit.unit_number.split('-')[2]}
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#6b7280' }}>
                                    {unit.bhk_type}
                                  </div>
                                  <div style={{
                                    fontSize: '10px',
                                    color: getStatusColor(unit.status),
                                    fontWeight: '600',
                                    marginTop: '2px',
                                    textTransform: 'uppercase'
                                  }}>
                                    {getStatusLabel(unit.status)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );

                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="card">
          <h2 className="card-title">Units ({filteredUnits.length})</h2>
          {loading ? (
            <div className="loading">Loading inventory...</div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Unit Number</th>
                    <th>Wing</th>
                    <th>Floor</th>
                    <th>Flat</th>
                    <th>BHK</th>
                    <th>Area (Sq Ft)</th>
                    <th>Agreement Value</th>
                    <th>Status</th>
                    <th>Price</th>
                    <th>Client</th>
                    <th>Remarks</th>
                    <th>Links</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUnits.map(unit => {
                    const area = unit.carpet_area || 0;
                    const agreementValue = area * 25000; // PSF rate: 25000
                    return (
                      <tr key={unit.id}>
                        <td><strong>{unit.unit_number}</strong></td>
                        <td>{unit.wing}</td>
                        <td>{unit.floor}</td>
                        <td>{unit.flat_number}</td>
                        <td>{unit.bhk_type}</td>
                        <td>
                          {area > 0 ? (
                            <div>
                              <div style={{ fontWeight: '500' }}>{formatNumber(area)}</div>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td>
                          {area > 0 ? (
                            <div>
                              <strong style={{ color: '#2563eb' }}>₹{formatCurrency(agreementValue)}</strong>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>
                                @ ₹25,000 PSF
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td>{getStatusBadge(unit.status)}</td>
                        <td>₹{formatCurrency(unit.final_price || unit.base_price || 0)}</td>
                        <td>
                          {unit.client_name ? (
                            <div>
                              <div>{unit.client_name}</div>
                              <small style={{ color: '#6b7280' }}>{unit.client_phone}</small>
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          {unit.remarks ? (
                            <span title={unit.remarks} style={{ cursor: 'pointer' }} onClick={() => handleAction(unit, 'remarks')}>
                              📝 {unit.remarks.substring(0, 30)}...
                            </span>
                          ) : (
                            <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 8px' }} onClick={() => handleAction(unit, 'remarks')}>
                              Add
                            </button>
                          )}
                        </td>
                        <td>
                          {(unit.hyperlinks || unit.cost_sheet_url || unit.floor_plan_url) ? (
                            <span style={{ cursor: 'pointer' }} onClick={() => handleAction(unit, 'links')}>
                              🔗 View
                            </span>
                          ) : (
                            <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 8px' }} onClick={() => handleAction(unit, 'links')}>
                              Add
                            </button>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {unit.status === 'available' && (
                              <>
                                <button className="btn btn-warning" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleAction(unit, 'block')}>
                                  Block
                                </button>
                                <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleAction(unit, 'book')}>
                                  Book
                                </button>
                              </>
                            )}
                            {unit.status === 'blocked' && (
                              <>
                                <button className="btn btn-success" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleUnblock(unit.id)}>
                                  Unblock
                                </button>
                                <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleAction(unit, 'book')}>
                                  Book
                                </button>
                              </>
                            )}
                            {unit.status === 'booked' && (
                              <button className="btn btn-danger" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleAction(unit, 'cancel')}>
                                Cancel
                              </button>
                            )}
                            <a
                              href={`/cost-sheet?project=${unit.project_id}&unit=${unit.id}`}
                              className="btn btn-sm btn-primary"
                              style={{ fontSize: '11px', padding: '4px 8px', textDecoration: 'none' }}
                              title="Generate Cost Sheet"
                            >
                              💰 Cost
                            </a>
                            <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => handleAction(unit, 'edit')}>
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredUnits.length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  {projects.length === 0 ? (
                    <div>
                      <p style={{ marginBottom: '16px' }}>No projects found. Please create a project first.</p>
                      <p style={{ fontSize: '14px' }}>The project tenant.name should be created automatically when the server starts.</p>
                    </div>
                  ) : !selectedProject ? (
                    <div>
                      <p style={{ marginBottom: '16px' }}>Please select a project from the dropdown above.</p>
                      <p style={{ fontSize: '14px' }}>Then click "Initialize Inventory" to create all units.</p>
                    </div>
                  ) : (
                    <div>
                      <p style={{ marginBottom: '16px' }}>No units found for this project.</p>
                      <p style={{ fontSize: '14px' }}>Click "Initialize Inventory (4 Wings x 35 Floors)" to create all 840 units.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && editingUnit && (
        <Modal
          unit={editingUnit}
          type={modalType}
          onClose={() => setShowModal(false)}
          onBlock={handleBlock}
          onBook={handleBook}
          onCancel={handleCancel}
          onUpdate={handleUpdate}
          onUpdateRemarks={handleUpdateRemarks}
          onUpdateHyperlinks={handleUpdateHyperlinks}
        />
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ unit, type, onClose, onBlock, onBook, onCancel, onUpdate, onUpdateRemarks, onUpdateHyperlinks }) => {
  const [formData, setFormData] = useState({
    blocked_until: '',
    remarks: unit.remarks || '',
    client_id: unit.client_id || '',
    sale_id: unit.sale_id || '',
    carpet_area: unit.carpet_area || '',
    base_price: unit.base_price || '',
    floor_rise: unit.floor_rise || '',
    premium: unit.premium || '',
    discount: unit.discount || '',
    final_price: unit.final_price || '',
    hyperlinks: unit.hyperlinks || '',
    cost_sheet_url: unit.cost_sheet_url || '',
    floor_plan_url: unit.floor_plan_url || '',
    agreement_value: (unit.carpet_area || 0) * 25000
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (type === 'block') {
      onBlock(unit.id, { blocked_until: formData.blocked_until, remarks: formData.remarks });
    } else if (type === 'book') {
      onBook(unit.id, { client_id: formData.client_id, sale_id: formData.sale_id, remarks: formData.remarks });
    } else if (type === 'cancel') {
      onCancel(unit.id, { remarks: formData.remarks });
    } else if (type === 'edit') {
      // Include area fields in update
      const updateData = {
        ...formData,
        carpet_area: formData.carpet_area || unit.carpet_area
      };
      onUpdate(unit.id, updateData);
    } else if (type === 'remarks') {
      onUpdateRemarks(unit.id, formData.remarks);
    } else if (type === 'links') {
      onUpdateHyperlinks(unit.id, {
        hyperlinks: formData.hyperlinks,
        cost_sheet_url: formData.cost_sheet_url,
        floor_plan_url: formData.floor_plan_url
      });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {type === 'block' && 'Block Unit'}
            {type === 'book' && 'Book Unit'}
            {type === 'cancel' && 'Cancel Booking'}
            {type === 'edit' && 'Edit Unit'}
            {type === 'remarks' && 'Add/Edit Remarks'}
            {type === 'links' && 'Manage Hyperlinks'}
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Unit: {unit.unit_number}</label>
          </div>

          {type === 'block' && (
            <>
              <div className="form-group">
                <label className="form-label">Block Until</label>
                <input
                  type="datetime-local"
                  value={formData.blocked_until}
                  onChange={(e) => setFormData({ ...formData, blocked_until: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="form-input"
                  rows="3"
                />
              </div>
            </>
          )}

          {type === 'book' && (
            <>
              <div style={{
                padding: '12px',
                background: '#eff6ff',
                borderRadius: '6px',
                marginBottom: '16px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                  Unit Agreement Value Calculation
                </div>
                <div style={{ fontSize: '13px', color: '#1e3a8a' }}>
                  <div>Area: <strong>{formatNumber(unit.carpet_area || 0)} Sq Ft</strong></div>
                  <div>PSF Rate: <strong>₹25,000</strong></div>
                  <div style={{ marginTop: '4px', fontSize: '15px', fontWeight: '600', color: '#2563eb' }}>
                    Agreement Value: ₹{formatCurrency(((unit.carpet_area || 0) * 25000))}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Client ID</label>
                <input
                  type="number"
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sale ID (Optional - will be created if not provided)</label>
                <input
                  type="number"
                  value={formData.sale_id}
                  onChange={(e) => setFormData({ ...formData, sale_id: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="form-input"
                  rows="3"
                />
              </div>
            </>
          )}

          {type === 'cancel' && (
            <div className="form-group">
              <label className="form-label">Cancellation Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                className="form-input"
                rows="3"
                required
              />
            </div>
          )}

          {type === 'edit' && (
            <>
              <div className="form-group">
                <label className="form-label">Carpet Area (Sq Ft)</label>
                <input
                  type="number"
                  step="0.01"
                  value={unit.carpet_area || ''}
                  onChange={(e) => {
                    const carpetArea = parseFloat(e.target.value) || 0;
                    const agreementValue = carpetArea * 25000;
                    setFormData({
                      ...formData,
                      carpet_area: carpetArea,
                      agreement_value: agreementValue
                    });
                  }}
                  className="form-input"
                  placeholder="Enter carpet area"
                />
                {unit.carpet_area && (
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#2563eb', fontWeight: '500' }}>
                    Agreement Value: ₹{formatCurrency((unit.carpet_area || 0) * 25000)} @ ₹25,000 PSF
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Base Price</label>
                <input
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Floor Rise</label>
                <input
                  type="number"
                  value={formData.floor_rise}
                  onChange={(e) => setFormData({ ...formData, floor_rise: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Premium</label>
                <input
                  type="number"
                  value={formData.premium}
                  onChange={(e) => setFormData({ ...formData, premium: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Discount</label>
                <input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Final Price</label>
                <input
                  type="number"
                  value={formData.final_price}
                  onChange={(e) => setFormData({ ...formData, final_price: e.target.value })}
                  className="form-input"
                />
              </div>
            </>
          )}

          {type === 'remarks' && (
            <div className="form-group">
              <label className="form-label">Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                className="form-input"
                rows="5"
              />
            </div>
          )}

          {type === 'links' && (
            <>
              <div className="form-group">
                <label className="form-label">Hyperlinks (JSON or comma-separated)</label>
                <textarea
                  value={formData.hyperlinks}
                  onChange={(e) => setFormData({ ...formData, hyperlinks: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder='{"label": "url"} or "url1, url2"'
                />
              </div>
              <div className="form-group">
                <label className="form-label">Cost Sheet URL</label>
                <input
                  type="url"
                  value={formData.cost_sheet_url}
                  onChange={(e) => setFormData({ ...formData, cost_sheet_url: e.target.value })}
                  className="form-input"
                  placeholder="https://..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Floor Plan URL</label>
                <input
                  type="url"
                  value={formData.floor_plan_url}
                  onChange={(e) => setFormData({ ...formData, floor_plan_url: e.target.value })}
                  className="form-input"
                  placeholder="https://..."
                />
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">
              {type === 'block' && 'Block'}
              {type === 'book' && 'Book'}
              {type === 'cancel' && 'Cancel'}
              {type === 'edit' && 'Update'}
              {type === 'remarks' && 'Save'}
              {type === 'links' && 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(amount);
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(num);
};

export default Inventory;

